import React, { useEffect, useState } from 'react';
import logoPC from './kok-batay-pc.png';
import logoAndroid from './kok-batay-android.png';

/**
 * Fullscreen preloader overlay with Kok Batay image and a progress bar.
 * Progress fills until window load completes (or a safety timeout),
 * then reaches 100% and notifies parent via onComplete.
 */
const Preloader = ({ onComplete, minDuration = 5000, timeout = 6000 }) => {
  const [progress, setProgress] = useState(0);
  const [logo, setLogo] = useState(logoPC);

  useEffect(() => {
    const updateLogo = () => {
      if (window.innerWidth < 768) {
        setLogo(logoAndroid);
      } else {
        setLogo(logoPC);
      }
    };

    updateLogo();
    window.addEventListener('resize', updateLogo);

    return () => window.removeEventListener('resize', updateLogo);
  }, []);

  useEffect(() => {
    let rafId;
    const start = Date.now();
    let ready = false;

    const markReady = () => {
      ready = true;
    };

    const onWindowLoad = () => markReady();
    window.addEventListener('load', onWindowLoad);

    if (document.readyState === 'complete') {
      markReady();
    }

    // Safety timeout to avoid being stuck forever
    const safety = setTimeout(markReady, timeout);

    const loop = () => {
      const elapsed = Date.now() - start;
      const minDone = elapsed >= minDuration;

      // Progress fills linearly to ~95% across the minimum duration
      const target = Math.min((elapsed / minDuration) * 95, 95);
      setProgress((p) => (p < target ? target : p));

      // Finish only when both: minimum duration elapsed AND page ready
      if (ready && minDone) {
        setProgress(100);
        setTimeout(() => {
          if (onComplete) onComplete();
        }, 250);
        cancelAnimationFrame(rafId);
        return;
      }

      rafId = requestAnimationFrame(loop);
    };

    rafId = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('load', onWindowLoad);
      clearTimeout(safety);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [onComplete, minDuration, timeout]);

  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center" style={{ backgroundColor: '#00042D' }}>
      <img
        src={logo}
        alt="Kok Batay"
        className="absolute inset-0 w-full h-full object-cover md:object-contain select-none"
        draggable="false"
      />
      <div className="absolute inset-x-0 bottom-6 md:bottom-12 flex justify-center px-4 md:px-6">
        <div className="w-full max-w-[90%] sm:max-w-md md:max-w-xl lg:max-w-2xl">
          <div className="h-2 w-full rounded-full bg-white/30 overflow-hidden backdrop-blur-sm">
            <div
              className="h-full bg-primary-500 transition-[width] duration-150 ease-out"
              style={{ width: `${Math.floor(progress)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preloader;
